import { sortedIndexBy } from "lodash";
import { SymmetricDifference } from "utility-types";
import { assertIs } from "@fidget/common/utils/ts-utils";
import { IAgent } from "../../../agent/Agent";
import { IGuest } from "../../../base/Guest";
import { IChatItem } from "../ChatItem";
import { ChatItemAssertion } from "../ChatItemAssertion";
import { ChatItemStatus } from "../ChatItemStatus";
import { ChatItemType } from "../ChatItemType";
import { IContactEnquiryChatItem } from "./ContactEnquiryChatItem";
import { IFakeUserTextChatItem } from "./FakeUserTextChatItem";
import { IMeetingChatItem } from "./MeetingChatItem";
import { IMeetingSchedulerChatItem } from "./MeetingSchedulerChatItem";
import { IUserTextChatItem } from "./UserTextChatItem";

type IGroupableItem =
  | IUserTextChatItem
  | IContactEnquiryChatItem
  | IFakeUserTextChatItem
  | IMeetingChatItem
  | IMeetingSchedulerChatItem;

type INonGroupableItem = SymmetricDifference<IChatItem, IGroupableItem>;

export const isChatItemGroupable = function (item: IChatItem): item is IGroupableItem {
  if (
    ChatItemAssertion.isUserText(item) ||
    ChatItemAssertion.isContactEnquiry(item) ||
    ChatItemAssertion.isFakeUserText(item) ||
    ChatItemAssertion.isMeeting(item) ||
    ChatItemAssertion.isMeetingScheduler(item)
  ) {
    assertIs<IGroupableItem>(item);
    return true;
  }

  assertIs<INonGroupableItem>(item);
  return false;
};

export class ChatItemGroup {
  public readonly type = ChatItemType.Group;
  private _startTimestamp: number;
  private _endTimestamp: number;
  private threshold: number;

  public readonly sortedItems: IGroupableItem[] = [];
  public readonly id: string;
  public readonly user: IAgent | IGuest;
  public readonly status: ChatItemStatus;

  constructor(chatItem: IGroupableItem, thresholdInSeconds = 600) {
    if (!isChatItemGroupable(chatItem)) {
      throw new Error(`${(chatItem as IGroupableItem).type} cannot create a group`);
    }
    this.sortedItems.push(chatItem);
    this.id = `group-${chatItem.id}`;
    this.user = chatItem.user;
    this.status = chatItem.status;
    this._startTimestamp = chatItem.timestamp;
    this._endTimestamp = chatItem.timestamp;
    this.threshold = thresholdInSeconds * 1000;
  }

  public get items(): ReadonlyArray<IGroupableItem> {
    return this.sortedItems;
  }

  public get timestamp() {
    return this._endTimestamp;
  }

  public get startTimestamp() {
    return this._startTimestamp;
  }

  public get endTimestamp() {
    return this._endTimestamp;
  }

  public get shown() {
    return true;
  }

  public canBeAdded(item: IChatItem): boolean {
    return (
      isChatItemGroupable(item) &&
      item.user.id === this.user.id &&
      item.status === this.status &&
      item.timestamp >= this.startTimestamp &&
      item.timestamp <= this.startTimestamp + this.threshold
    );
  }

  public add(item: IGroupableItem): boolean {
    if (!this.canBeAdded(item)) {
      return false;
    }

    this.sortedItems.splice(sortedIndexBy(this.sortedItems, item, "timestamp"), 0, item);
    this._startTimestamp = this.sortedItems[0]!.timestamp;
    this._endTimestamp = this.sortedItems[this.sortedItems.length - 1]!.timestamp;

    return true;
  }
}
